import clsx from "clsx"

import { faIcon } from "@utils/faIcon"

import type { ObjectStatusAddOn } from "@atoms/Avatar/Avatar.types"
import { Avatar } from "@atoms/index"

import { objectMap } from "@constants/objectMap"
import { getObjectStatusStyle } from "@constants/objectStatusStyles"

import styles from "./ObjectAvatar.module.scss"
import { ObjectAvatarProps } from "./ObjectAvatar.types"
import { clientAvatarIcons, hasObjectType } from "./ObjectAvatar.utils"

function getStatusAddOn(props: ObjectAvatarProps): ObjectStatusAddOn | undefined {
    if ("status" in props && props.status) {
        const { colorScheme, icon, iconRotation } = getObjectStatusStyle(
            props.objectType,
            props.status,
            props.objectType === "job" ? props.statusColorOverride : undefined,
        )
        return {
            color: `var(--${colorScheme}-a9)`,
            icon: faIcon({ name: icon, style: "solid" }),
            rotation: iconRotation,
        }
    } else {
        return undefined
    }
}

export default function ObjectAvatar(props: ObjectAvatarProps) {
    const { objectType, size, isLoading, tooltip, baseClassName, iconClassName } = props

    const src = hasObjectType(props, "vendor", "client", "product") ? props.src : undefined
    const fallbackName = hasObjectType(props, "vendor", "client", "product") ? props.fallbackName : undefined
    const icon = hasObjectType(props, "client")
        ? clientAvatarIcons[props.clientType ?? "business"]
        : objectMap[objectType].iconActive
    const statusAddOn = getStatusAddOn(props)

    const baseProps = {
        size,
        isLoading,
        tooltip,
        // File avatars keep light-mode colors in every theme; the loading skeleton ignores this and follows the theme
        baseClassName: objectType === "file" ? clsx("light-theme", styles.file, baseClassName) : baseClassName,
        iconClassName,
        ...(statusAddOn && { addOnStatus: statusAddOn, addOnType: "icon" as const }),
    }

    return src || fallbackName ? (
        <Avatar variant="company" src={src} fallbackName={fallbackName ?? ""} {...baseProps} />
    ) : (
        <Avatar variant="object" icon={icon} {...baseProps} />
    )
}
